'use client';

import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from 'react-simple-maps';
import avatecLogo from '../../assets/logos/avatec-white.png';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

type OfficeItem = {
  id: string;
  name: string;
  location: string;
  address?: string;
  email?: string;
  email2?: string;
  phone?: string;
  image: string;
  lat: number;
  lng: number;
};

const offices: OfficeItem[] = [
  {
    id: 'nl',
    name: 'Avatec Marine B.V.',
    location: 'Rotterdam, Netherlands',
    address: 'Dalweg 3B, 3233 KK Oostvoorne, Rotterdam, Netherlands',
    email: 'service.nl@avatecmarine.com',
    phone: '+31 627303793',
    image: 'https://images.squarespace-cdn.com/content/v1/61e3506787127058e41e3ac4/353923cf-a026-4735-965c-313566a72868/unsplash-image-fJXvxovQ2Bg.jpg',
    lat: 51.9225,
    lng: 4.4792,
  },
  {
    id: 'de',
    name: 'Avatec Marine GmbH',
    location: 'Hamburg, Germany',
    address: 'Schimmelmannstr. 101, 22043 Hamburg, Germany',
    email: 'service.de@avatecmarine.com',
    phone: '+49 162 434 0 222',
    image: 'https://images.squarespace-cdn.com/content/v1/61e3506787127058e41e3ac4/1c817521-876b-42ea-baf8-18794b19cdb8/unsplash-image-m4nF5RftfdY.jpg',
    lat: 53.5511,
    lng: 9.9937,
  },
  {
    id: 'fi',
    name: 'Avatec Marine Finland Oy',
    location: 'Helsinki, Finland',
    address: 'Konepajankuja 1, 00510 Helsinki, Finland',
    email: 'service.fi@avatecmarine.com',
    phone: '+358 46 680 2700',
    image: 'https://images.squarespace-cdn.com/content/v1/61e3506787127058e41e3ac4/1f00a57c-45e5-496e-bd92-0c59f768a427/suomi-or-finland-beautiful-red-finnish-wooden-log-2021-09-03-17-14-23-utc.jpg',
    lat: 60.1699,
    lng: 24.9384,
  },
  {
    id: 'se',
    name: 'Avatec Marine AB',
    location: 'Gothenburg, Sweden',
    address: 'Datavagen 14B 436 32 Askim Gothenburg, Sweden',
    email: 'service.se@avatecmarine.com',
    phone: '+46 701767199',
    image: 'https://images.squarespace-cdn.com/content/v1/61e3506787127058e41e3ac4/1765788533394-1UBYP4ZBD7H1CERE7S5S/unsplash-image-5Gqx11weVJI.jpg',
    lat: 57.7089,
    lng: 11.9746,
  },
  {
    id: 'ee',
    name: 'Avatec Marine OÜ',
    location: 'Tallinn, Estonia',
    address: 'Kadaka tee 3/2, 10621 Tallinn, Estonia',
    email: 'service.ee@avatecmarine.com',
    phone: '+372 600 2299',
    image: 'https://images.squarespace-cdn.com/content/v1/61e3506787127058e41e3ac4/1765788693987-J7T1XDPHY72A1XCGD9BC/unsplash-image-G58T5XKHO14.jpg',
    lat: 59.437,
    lng: 24.7536,
  },
  {
    id: 'uae-fzc',
    name: 'Avatec Marine FZC',
    location: 'Fujairah, UAE',
    address: 'No: 6, Block B, Phase II, PO Box:51228, Fujairah Free Zone Fujairah, United Arab Emirates',
    email: 'service.me@avatecmarine.com',
    phone: '+971 92282978',
    image: 'https://images.squarespace-cdn.com/content/v1/61e3506787127058e41e3ac4/1691581932900-I3M8QUMJ0R3JM7BARGKE/image-asset.jpeg',
    lat: 25.1288,
    lng: 56.3265,
  },
  {
    id: 'lt',
    name: 'Avatec Marine UAB',
    location: 'Klaipeda, Lithuania',
    address: 'Klaipeda, Lithuania',
    email: 'service@avatecmarine.com',
    phone: '+372 600 2299',
    image: 'https://images.squarespace-cdn.com/content/v1/61e3506787127058e41e3ac4/078abcb8-fefb-43fe-aa5b-f4c3ff8f2664/unsplash-image-P-hRe4b8b9g.jpg',
    lat: 55.7033,
    lng: 21.1443,
  },
  {
    id: 'uae-llc',
    name: 'Avatec Marine Automation Service LLC',
    location: 'Dubai, UAE',
    address: 'Plot no. 246-5905, Saud Talal warehouse, No. 8, AL Qusais-3, Dubai, United Arab Emirates',
    email: 'service.me@avatecmarine.com',
    phone: '+971 4 552 7694',
    image: 'https://images.squarespace-cdn.com/content/v1/61e3506787127058e41e3ac4/1765788402211-20XJT1SJDA3VF6DAS6J4/image-asset.jpeg',
    lat: 25.2048,
    lng: 55.2708,
  },
  {
    id: 'uae-abu-dhabi',
    name: 'Avatec Marine LLC',
    location: 'Abu Dhabi, UAE',
    address: 'C42, E5, Al Danah, Abu Dhabi, UAE',
    email: 'sales.tr@avatecmarine.com',
    email2: 'service.me@avatecmarine.com',
    image: 'https://images.squarespace-cdn.com/content/v1/61e3506787127058e41e3ac4/1765788402211-20XJT1SJDA3VF6DAS6J4/image-asset.jpeg',
    lat: 24.4539,
    lng: 54.3773,
  },
  {
    id: 'tr',
    name: 'Avatec Marine Service Ltd. Sti.',
    location: 'Istanbul, Türkiye',
    address: 'Güzelyali mah. Alinteri Sok. No:2 Pendik Istanbul Türkiye',
    email: 'sales.tr@avatecmarine.com',
    phone: '+90 216 296 03 22',
    image: 'https://images.squarespace-cdn.com/content/v1/61e3506787127058e41e3ac4/ed9a9ede-0a1c-4a92-8186-e93e49f3f82d/unsplash-image-WA1u0scVLZU.jpg',
    lat: 41.0082,
    lng: 28.9784,
  },
  {
    id: 'pl',
    name: 'Avatec Marine Automation Sp. z o.o.',
    location: 'Szczecin, Poland',
    address: 'al. Wyzwolenia 46, office 232, 71-899 Szczecin Poland',
    email: 'service.pl@avatecmarine.com',
    phone: '+48 600 970 758',
    image: 'https://images.squarespace-cdn.com/content/v1/61e3506787127058e41e3ac4/fd8b9b7d-69b8-4f3c-8bcf-72abc6e16b97/1000_F_116473719_Ct2EYrpOAlZoUtNX74DfkWWHoAZZLvoO.jpg',
    lat: 53.4285,
    lng: 14.5528,
  },
  {
    id: 'pa',
    name: 'Avatec Marine, S.A',
    location: 'Panama',
    address: 'Edificio 3845-Piso 02, Avatec Marine, S.A, Panama',
    image: 'https://images.unsplash.com/photo-1560813962-ff3d8fcf59ba?auto=format&fit=crop&w=800&q=80',
    lat: 8.9936,
    lng: -79.5197,
  },
  {
    id: 'ma',
    name: 'Avatec Marine Morocco Sarl',
    location: 'Agadir, Morocco',
    address: 'N70 HO24 Street, Block A Al Houda District, Agadir Morocco',
    email: 'service.ma@avatecmarine.com',
    phone: '+212 674 800 330',
    image: 'https://images.squarespace-cdn.com/content/v1/61e3506787127058e41e3ac4/1691581593364-E03Q1ZYQ0A0Z0T5178IS/image-asset.jpeg',
    lat: 30.4278,
    lng: -9.5981,
  },
  {
    id: 'in',
    name: 'Avatec Marine Private Limited',
    location: 'Navi Mumbai, India',
    address: 'Kamdhenu Commerz Business Park, Office 2008, Sector 14, Plot no. 2, Kharghar, Navi Mumbai, India',
    email: 'service.asia@avatecmarine.com',
    phone: '+91 2248 25 9805',
    image: 'https://images.squarespace-cdn.com/content/v1/61e3506787127058e41e3ac4/b2180a74-a252-4f00-94e6-68e8f1e3913a/unsplash-image-YtfUqAPLqMk.jpg',
    lat: 19.076,
    lng: 72.8777,
  },
  {
    id: 'mt',
    name: 'Avatec Marine Malta Ltd.',
    location: 'Valletta, Malta',
    image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b7/St_Sebastian_Curtain_%28cropped%29.jpg/1280px-St_Sebastian_Curtain_%28cropped%29.jpg',
    lat: 35.8989,
    lng: 14.5146,
  },
  {
    id: 'cn',
    name: 'Avatec Marine China Co., Ltd.',
    location: 'China',
    image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/be/SuperStar_Aquarius_at_Phoenix_Island%2C_Sanya_Bay_-_01.jpg/1280px-SuperStar_Aquarius_at_Phoenix_Island%2C_Sanya_Bay_-_01.jpg',
    lat: 31.2304,
    lng: 121.4737,
  },
];

const PAGE_SIZE = 4;
const CARD_PAGES = 4;

function OfficeCard({
  office,
  active,
  onToggle,
}: {
  office: OfficeItem;
  active: boolean;
  onToggle: () => void;
}) {
  const reduceMotion = useReducedMotion();

  return (
    <button
      type="button"
      onClick={onToggle}
      className="group relative h-[180px] w-full overflow-hidden rounded-[26px] border border-white/10 bg-white/[0.04] text-left shadow-[0_20px_60px_rgba(0,0,0,0.35)] transition hover:border-white/20 sm:h-[200px]"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${office.image})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050d18]/95 via-[#050d18]/55 to-[#050d18]/10" />

      <div className="relative z-10 flex h-full flex-col justify-end p-5">
        <div className="mt-1 text-lg font-semibold text-white">{office.name}</div>
        <div className="text-sm text-white/70">{office.location}</div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            className="absolute inset-0 z-20 flex h-full w-full flex-col justify-between bg-[#0a1628]/90 p-5 text-white/85 backdrop-blur"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.25 }}
          >
            <div>
              <div className="text-base font-semibold text-white">{office.name}</div>
              {office.address && (
                <div className="mt-2 text-sm leading-relaxed text-white/80">{office.address}</div>
              )}
            </div>
            <div className="space-y-1 text-sm">
              {office.email && <div className="text-white/70">{office.email}</div>}
              {office.email2 && <div className="text-white/70">{office.email2}</div>}
              {office.phone && <div className="text-white/70">{office.phone}</div>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </button>
  );
}

function WorldMapView({
  hoveredOffice,
  onHover,
}: {
  hoveredOffice: string | null;
  onHover: (id: string | null) => void;
}) {
  return (
    <div className="flex flex-1 items-center justify-center">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ center: [25, 40], scale: 380 }}
        style={{ width: '100%', height: '100%', maxHeight: '520px' }}
      >
        <ZoomableGroup>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="rgba(255,255,255,0.06)"
                  stroke="rgba(255,255,255,0.12)"
                  strokeWidth={0.4}
                  style={{
                    default: { outline: 'none' },
                    hover: { outline: 'none', fill: 'rgba(255,255,255,0.1)' },
                    pressed: { outline: 'none' },
                  }}
                />
              ))
            }
          </Geographies>

          {offices.map((office) => {
            const isHovered = hoveredOffice === office.id;
            return (
              <Marker
                key={office.id}
                coordinates={[office.lng, office.lat]}
                onMouseEnter={() => onHover(office.id)}
                onMouseLeave={() => onHover(null)}
              >
                <circle r={isHovered ? 12 : 6} fill="none" stroke="rgba(59,130,246,0.4)" strokeWidth="1.5">
                  <animate attributeName="r" values={isHovered ? '6;16;6' : '4;8;4'} dur="2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.7;0;0.7" dur="2s" repeatCount="indefinite" />
                </circle>
                <circle r={isHovered ? 8 : 4} fill="rgba(59,130,246,0.2)" />
                <circle
                  r={isHovered ? 5 : 3}
                  fill="#3b82f6"
                  stroke="white"
                  strokeWidth={isHovered ? 1.5 : 0.8}
                  style={{ cursor: 'pointer', transition: 'r 0.2s' }}
                />
                {isHovered && (
                  <g>
                    <rect
                      x={10}
                      y={-14}
                      width={office.location.length * 6.5 + 20}
                      height={24}
                      rx={5}
                      fill="rgba(10,22,40,0.95)"
                      stroke="rgba(59,130,246,0.4)"
                      strokeWidth={0.8}
                    />
                    <text
                      x={20}
                      y={2}
                      fill="white"
                      fontSize={11}
                      fontFamily="system-ui, sans-serif"
                      fontWeight={500}
                    >
                      {office.location}
                    </text>
                  </g>
                )}
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>
    </div>
  );
}

// step: 0,1,2 = kart sayfaları | 3 = dünya haritası
export default function Slide04Offices({ step = 0 }: { step?: number }) {
  const reduceMotion = useReducedMotion();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [hoveredOffice, setHoveredOffice] = useState<string | null>(null);

  const showMap = step >= CARD_PAGES;
  const page = Math.min(step, CARD_PAGES - 1);

  const cards = useMemo(
    () =>
      page === CARD_PAGES - 1
        ? offices.slice(page * PAGE_SIZE)
        : offices.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [page]
  );

  // Sayfa değişince aktif kartı sıfırla
  React.useEffect(() => {
    setActiveId(null);
  }, [step]);

  return (
    <motion.div
      className="slide relative flex h-screen w-full overflow-hidden bg-[#050d18] text-white"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: reduceMotion ? 0 : 0.8, ease: 'easeOut' }}
    >
      <img src={avatecLogo} alt="Avatec Marine" className="slide-logo" />

      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_30%_0%,rgba(60,120,190,0.18),transparent_65%),linear-gradient(180deg,rgba(5,13,24,0.92)_0%,rgba(5,13,24,0.98)_50%,rgba(5,13,24,1)_100%)]" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
        }}
      />

      <div className="relative z-10 mx-auto flex w-full max-w-[1400px] flex-1 flex-col px-4 pb-6 pt-16 sm:px-[4vw] sm:pb-10 sm:pt-20">
        {/* Header */}
        <div className="mb-5 sm:mb-8">
          <h1 className="text-2xl font-semibold tracking-[0.14em] sm:text-3xl sm:tracking-[0.2em]">OUR OFFICES WORLDWIDE</h1>
          <p className="mt-2 text-xs text-white/70 sm:text-sm">
            Regional locations supporting shipowners, operators & shipyards.
          </p>
        </div>

        <AnimatePresence mode="wait">
          {showMap ? (
            <motion.div
              key="map"
              className="flex flex-1 flex-col"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.3 }}
            >
              <WorldMapView hoveredOffice={hoveredOffice} onHover={setHoveredOffice} />
              <div className="mt-4 flex flex-wrap justify-center gap-3">
                {offices.map((office) => (
                  <button
                    key={office.id}
                    type="button"
                    onMouseEnter={() => setHoveredOffice(office.id)}
                    onMouseLeave={() => setHoveredOffice(null)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                      hoveredOffice === office.id
                        ? 'border-blue-400/40 bg-blue-500/15 text-blue-300'
                        : 'border-white/10 bg-white/[0.04] text-white/60 hover:border-white/20 hover:text-white/80'
                    }`}
                  >
                    {office.location}
                  </button>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key={`cards-${page}`}
              className="flex flex-1 flex-col"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.3 }}
            >
              <div className="grid flex-1 auto-rows-min content-start grid-cols-1 gap-7 md:grid-cols-2">
                {cards.map((office, idx) => (
                  <motion.div
                    key={office.id}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: reduceMotion ? 0 : 0.1 + idx * 0.04, duration: 0.6, ease: 'easeOut' }}
                  >
                    <OfficeCard
                      office={office}
                      active={activeId === office.id}
                      onToggle={() => setActiveId((prev) => (prev === office.id ? null : office.id))}
                    />
                  </motion.div>
                ))}
              </div>

              {/* Sayfa göstergesi (sadece noktalar, buton yok) */}
              <div className="mt-6 flex items-center justify-center">
                <div className="flex items-center gap-2">
                  {Array.from({ length: CARD_PAGES }).map((_, i) => (
                    <span
                      key={i}
                      className={`h-2 w-2 rounded-full transition-colors ${i === page ? 'bg-white/70' : 'bg-white/20'}`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
